import type { ResultOf } from '@graphql-typed-document-node/core';

import Skeleton from '@mui/material/Skeleton';

import {
  getFragmentData,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType } from '@e-socna/data-layer';

import { formatBoolean } from '../../utils/format-boolean.util';
import { wrapExpression } from '../../utils/handlebars-expression.util';
import { N_A } from '../../constants/labels.constant';

import { useContentGenerator } from '../DocumentEditor/hooks/useContentGenerator.hook';

import { useResolvableNormalizer } from './hooks/useResolvableNormalizer';
import { PrimaryWrapper as PrimaryWrapperCmp } from './PrimaryWrapper';
import { SecondaryWrapper as SecondaryWrapperCmp } from './SecondaryWrapper';
import type { Props as BaseProps } from './types/props.type';

export const ResolvableBooleanCmp_ResolvableBoolean_Fragment = graphql(`
  fragment ResolvableBooleanCmp_ResolvableBoolean_Fragment on ResolvableBoolean {
    __typename
    mode
    resolverId
    value
  }
`);

export type ResolvableBooleanCmp_ResolvableBoolean = ResultOf<
  typeof ResolvableBooleanCmp_ResolvableBoolean_Fragment
>;

interface Props extends BaseProps {
  readonly resolvableBoolean: FragmentType<
    typeof ResolvableBooleanCmp_ResolvableBoolean_Fragment
  >;
}

export const ResolvableBoolean = ({
  PrimaryWrapper = PrimaryWrapperCmp,
  resolvableBoolean: pResolvableBoolean,
  resolvableFieldSettings,
  SecondaryWrapper = SecondaryWrapperCmp,
}: Readonly<Props>) => {
  const resolvableBoolean = getFragmentData(
    ResolvableBooleanCmp_ResolvableBoolean_Fragment,
    pResolvableBoolean,
  );

  const normalizedBoolean = useResolvableNormalizer(resolvableBoolean);

  const { enableDirectGeneration, primaryMode } = resolvableFieldSettings;

  const { data: booleanData, isSuccess: booleanIsSuccess } =
    useContentGenerator({
      enabled:
        normalizedBoolean.mode === ResolvableModeEnum.Dynamic &&
        enableDirectGeneration,
      template:
        normalizedBoolean.mode === ResolvableModeEnum.Dynamic
          ? wrapExpression(normalizedBoolean.resolverCode)
          : EMPTY_STRING,
    });

  if (normalizedBoolean.mode === ResolvableModeEnum.Static) {
    if (!isDefined(normalizedBoolean.value)) {
      return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
    }

    return (
      <PrimaryWrapper>{formatBoolean(normalizedBoolean.value)}</PrimaryWrapper>
    );
  }

  const command = wrapExpression(normalizedBoolean.merged);

  if (!enableDirectGeneration) {
    return <PrimaryWrapper>{command}</PrimaryWrapper>;
  }

  if (!booleanIsSuccess) {
    return (
      <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />
    );
  }

  const valueAsBoolean = isDefined(booleanData?.generated)
    ? booleanData.generated === 'true'
    : normalizedBoolean.value;

  if (!isDefined(valueAsBoolean)) {
    return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
  }

  const formattedValue = formatBoolean(valueAsBoolean);

  return primaryMode === ResolvableModeEnum.Static ? (
    <>
      <PrimaryWrapper>{formattedValue}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{command}</SecondaryWrapper>
    </>
  ) : (
    <>
      <PrimaryWrapper>{command}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{formattedValue}</SecondaryWrapper>
    </>
  );
};
